<template>
  <DetailInfo hideEmptyBtnList class="tag-container">
    <div v-rect-loading="candidateTagLoading" class="tag-content">
      <TagEditor
        isSmart
        class="smart-tag"
        :title="$t(`${i18nPrefix}.title.smartTags`)"
        :tagList="tagListInfo.smart.list"
        :tagTypes="tagTypes"
        :enableEdit="hasCRMTagPermission"
        :tagListLoader="loadSmartTag"
        :isDisabled="isDisabled"
        @save="handleSave"
        @delete="handleDelete"
      >
      </TagEditor>
      <TagEditor
        hiddenAddTagBtn
        class="manual-tag"
        :hiddenAddBtn="hiddenAddBtn"
        :title="$t(`${i18nPrefix}.title.manualTags`)"
        :tagList="tagListInfo.manual.list"
        :tagTypes="tagTypes"
        :enableEdit="hasCRMTagPermission"
        :tagListLoader="loadManualTag"
        :isDisabled="isDisabled"
        @save="handleSave"
        @delete="handleDelete"
      >
      </TagEditor>
    </div>
    <CompanySurvey v-if="candidateCompanySurveyInfo && !needHiddenTag" :questionList="preferenceQuestionList" :notification="candidateCompanySurveyInfo.notification"/>
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import DetailInfo from './components/detailInfo.vue'
import TagEditor from './components/tagEditor.vue'
import { mapActions } from 'vuex'
import { getI18nPropertyValue, getConstantI18nValue, addI18nProperty } from '@/i18n'
import CompanySurvey from './components/companySurvey.vue'
import { JOB_TYPE, ROLE_TYPE } from '@/constant/meta'
import { TAG_TYPE_LIST } from '@/constant/crm'

export default {
  components: {
    DetailInfo,
    TagEditor,
    CompanySurvey
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      candidateTagLoading: false,
      candidateTagList: [],
      selectedTagIds: [],
      tagListInfo: {
        smart: {
          loading: false,
          loaded: false,
          list: []
        },
        manual: {
          loading: false,
          loaded: false,
          list: []
        }
      },
      candidateCompanySurveyInfo: null,
      tagTypes: ['Opened', 'Shared', 'Applied for', 'Others']
    }
  },
  computed: {
    needHiddenTag () {
      if (this.isPotential) {
        return !this.isTalentPool
      }
      return false
    },
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job?.id
    },
    hiddenAddBtn () {
      return !this.hasAnyPermission('PERM_TAG_MANAGEMANT')
    },
    hasParseResumeTagPermission () {
      return this.hasAnyPermission('PREMIUM_CRM_PARSE_RESUME_SDK_TAG')
    },
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    preferenceQuestionList () {
      return _.map(this.candidateCompanySurveyInfo?.question, item => {
        let answer = null
        if (['city', 'filter', 'custom'].includes(item.option_type)) {
          const result = item.question_type === 'checkbox' ? item.result : [item.result]
          answer = _.map(result, answer => getI18nPropertyValue(answer, 'cn_name', 'name')).join(',')
        }
        if (item.option_type === 'job_type') {
          answer = _.map(item.result.split(','), jobType => getConstantI18nValue(JOB_TYPE, jobType, 'meta.JOB_TYPE')).join(',')
        }
        if (item.option_type === 'role_type') {
          answer = _.map(item.result.split(','), roleType => getConstantI18nValue(ROLE_TYPE, roleType, 'meta.ROLE_TYPE')).join(',')
        }
        if (item.question_type === 'text') {
          answer = item.result
        }
        return {
          answer,
          i18nName: getI18nPropertyValue(item, 'cn_title', 'en_title')
        }
      })
    }
  },
  created () {
    this.loadCandidateTagInfo()
    this.loadCandidateCompanySurveyInfo()
  },
  methods: {
    ...mapActions('application', ['getApplicationCandidateTagInfo', 'getApplicationCandidateSurveyInfo']),
    ...mapActions('crm', ['searchTag', 'saveCandidateTag', 'deleteCandidateTag']),
    ...mapActions('talent', ['getTalentCandidateTagInfo', 'getTalentCandidateSurveyInfo']),
    ...mapActions('potential', ['getPotentialCandidateTagInfo', 'getPotentialCandidateSurveyInfo']),
    async loadCandidateTagInfo () {
      this.candidateTagLoading = true
      let candidateTagList = null
      try {
        if (this.isPotential) {
          const resp = await this.getPotentialCandidateTagInfo({ potentialId: this.candidateBaseInfo.potential_id })
          if (resp) {
            candidateTagList = resp
          }
        } else if (this.isTalentPool) {
          const resp = await this.getTalentCandidateTagInfo({ candidateId: this.candidateId })
          if (resp) {
            candidateTagList = resp
          }
        } else {
          const params = {
            job_id: this.jobId,
            candidate_id: this.candidateId
          }
          const resp = await this.getApplicationCandidateTagInfo(params)
          if (resp) {
            candidateTagList = resp
          }
        }
        if (!candidateTagList) {
          return
        }

        this.tagTypes = _.map(TAG_TYPE_LIST, tag => _.toUpper(tag))
        const selectedTagIds = []
        const smartTagList = []
        const manualTagList = []
        _.forEach(candidateTagList, tag => {
          tag.type = tag.type.toUpperCase()
          tag.isSelected = true
          addI18nProperty(tag, 'i18nName', 'cn_name', 'name')
          if (tag.content_cn && tag.content_en) {
            addI18nProperty(tag, 'i18nContent', 'content_cn', 'content_en')
          }
          selectedTagIds.push(tag.id)
          if (['smart', 'system', 'campaign'].includes(tag.category)) {
            smartTagList.push(tag)
          } else if (tag.category === 'manual') {
            manualTagList.push(tag)
          }
        })
        this.tagListInfo.smart.list = smartTagList
        this.tagListInfo.manual.list = manualTagList
        this.selectedTagIds = selectedTagIds
        this.candidateTagList = candidateTagList
      } finally {
        this.candidateTagLoading = false
      }
    },
    async loadCandidateCompanySurveyInfo () {
      if (this.isPotential) {
        const resp = await this.getPotentialCandidateSurveyInfo({ potentialId: this.candidateBaseInfo.potential_id })
        if (resp) {
          this.candidateCompanySurveyInfo = resp
        }
      } else if (this.isTalentPool) {
        const resp = await this.getTalentCandidateSurveyInfo({ candidateId: this.candidateId })
        if (resp) {
          this.candidateCompanySurveyInfo = resp
        }
      } else {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId,
          survey_type: 'company_survey'
        }
        const resp = await this.getApplicationCandidateSurveyInfo(params)
        if (resp) {
          this.candidateCompanySurveyInfo = resp
        }
      }
    },
    loadSmartTag () {
      return this.loadTag(true)
    },
    loadManualTag () {
      return this.loadTag(false)
    },
    async loadTag (isSmart) {
      if (!this.hasCRMTagPermission) {
        return []
      }
      const tagListInfo = this.tagListInfo[isSmart ? 'smart' : 'manual']
      if (tagListInfo.loading || tagListInfo.loaded) {
        return tagListInfo.list
      }
      tagListInfo.loading = true
      try {
        const params = {}
        if (!this.hasParseResumeTagPermission) {
          params.is_company_tag = 1
        }
        if (isSmart) {
          params.tag_category = _.join(['smart', 'system', 'campaign'], ',')
        } else {
          params.tag_category = 'manual'
        }
        const resp = await this.searchTag(params)
        const tagList = []
        const selectedTagIds = this.selectedTagIds
        _.forEach(resp, tag => {
          const isSelected = selectedTagIds.includes(tag.id)
          if (isSelected) {
            return
          }
          tag.type = tag.type.toUpperCase()
          tag.isSelected = false
          tag.operator = { id: null, name: null }
          tag.operate_time = null
          addI18nProperty(tag, 'i18nName', 'cn_name', 'name')
          if (tag.content_cn && tag.content_en) {
            addI18nProperty(tag, 'i18nContent', 'content_cn', 'content_en')
          }
          tagList.push(tag)
        })
        tagListInfo.list.push(...tagList)
      } finally {
        tagListInfo.loading = false
        tagListInfo.loaded = true
      }
      return tagListInfo.list
    },
    async handleSave (isSmart) {
      this.candidateTagLoading = true
      try {
        const tagListInfo = this.tagListInfo[isSmart ? 'smart' : 'manual']
        const tagList = tagListInfo.list
        const deleteTagIdList = _.map(_.filter(tagList, { isSelected: false }), 'id')
        const addTagList = _.filter(tagList, tag => tag.isSelected === true)

        _.forEach(addTagList, item => {
          const isSelected = _.findIndex(this.candidateTagList, { id: item.id }) > -1
          if (!isSelected) {
            this.candidateTagList.push(item)
          }
        })
        this.candidateTagList = _.filter(this.candidateTagList, item => !deleteTagIdList.includes(item.id))

        const params = {
          add_tags: _.map(addTagList, 'id'),
          delete_tags: deleteTagIdList,
          candidate_type: this.isPotential ? 'potential' : 'candidate'
        }
        await this.saveCandidateTag({ candidateId: this.isPotential ? this.candidateBaseInfo.potential_id : this.candidateId, params })
      } finally {
        this.candidateTagLoading = false
      }
    },
    async handleDelete (tagId, isSmart) {
      this.candidateTagLoading = true
      const tagListInfo = this.tagListInfo[isSmart ? 'smart' : 'manual']
      const tagList = tagListInfo.list
      const tag = _.find(tagList, { id: tagId })
      const data = {
        delete_tags: [tagId],
        candidate_type: this.isPotential ? 'potential' : 'candidate'
      }
      try {
        const resp = await this.deleteCandidateTag({
          candidateId: this.isPotential ? this.candidateBaseInfo.potential_id : this.candidateId,
          data
        })
        if (resp) {
          tag.isSelected = false
          this.$message.success(this.$t(`${this.i18nPrefix}.message.deleteSuccess`))
        }
      } finally {
        this.candidateTagLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-container {
  .smart-tag, .manual-tag {
    display: inline-block;
    vertical-align: top;
    margin: 0 20px;
    width: calc(50% - 40px);
  }
}

</style>
